import type { NextPage } from 'next';
import { useRouter } from 'next/router';

import {
  DatabaseIcon,
  LockClosedIcon,
  TerminalIcon
} from '@heroicons/react/outline';
import Head from 'next/head';
import { NextSeo } from 'next-seo';

import {
  BottomGraphic,
  CoverageSection,
  FadeIn,
  FaqSection,
  Footer,
  GradientBg,
  Header,
  InsuranceSection,
  PageHeader,
  WhoNeedsInsuranceSection,
  WhyIsInsuranceImportant
} from '../components';

const coverageItems = [
  {
    title: 'Data Breach',
    description:
      ' In the event that someone gains unauthorized access to sensitive or confidential business information, a cyber liability policy would cover the costs associated with defending or correcting these breaches ',
    icon: DatabaseIcon
  },
  {
    title: 'System Hacks',
    description: `Any business regardless of its size faces cyber security threats. If a cyber-criminal gains access to your business' private infrastructure or back-end system, associated financial losses for your business or a third-party can be covered.`,
    icon: TerminalIcon
  },
  {
    title: 'Ransomware',
    description:
      'If malicious malware is installed on your business devices or back-end system, financial losses associated with regaining access can be covered under a cyber insurance policy',
    icon: LockClosedIcon
  }
];
const faqs = [
  {
    question: 'Are there any requirements for cyber liability insurance?',
    answer:
      'Insurance requirements vary by state and industry. However, your business can be exposed to a high level of cyber risk if you are not properly protected. You may also lose potential clients and customers if you are asked to show proof of insurance and are not able to provide evidence of coverage. In short, although it might not be legally required, a cyber liability policy is crucial to protect your business from everyday virtual threats'
  },
  {
    question: 'Does cyber liability cover cyber ransom and attacks?',
    answer:
      'This depends on your insurance carrier. But in most cases the insurance company will cover the cost of the ransom rather than dispute the cyber attack'
  }
];

const Cyber: NextPage = () => {
  const router = useRouter();
  const canonicalUrl = (
    `https://www.coverdash.com` + (router.asPath === '/' ? '' : router.asPath)
  ).split('?')[0];
  return (
    <>
      <NextSeo
        title="Cyber Liability"
        canonical={canonicalUrl}
        openGraph={{
          url: canonicalUrl,
          title: 'Cyber Liability'
        }}
      />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "Are there any requirements for cyber liability insurance?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Insurance requirements vary by state and industry. However, your business can be exposed to a high level of cyber risk if you are not properly protected. You may also lose potential clients and customers if you are asked to show proof of insurance and are not able to provide evidence of coverage. In short, although it might not be legally required, a cyber liability policy is crucial to protect your business from everyday virtual threats."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Does cyber liability cover cyber ransom and attacks?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "This depends on your insurance carrier. But in most cases the insurance company will cover the cost of the ransom rather than dispute the cyber attack."
                  }
                }
              ]
            })
          }}
        />
      </Head>
      <GradientBg />
      <Header />
      <PageHeader
        title="Cyber"
        image={
          <>
            <img
              src="/img/cyber.svg"
              className="hidden sm:block mx-auto max-w-4xl w-full mt-8 md:mt-0"
              alt="cyber hero image"
            />
            <img
              src="/img/sb3.jpg"
              className="sm:hidden w-full max-w-4xl shadow rounded overflow-hidden"
              alt="cyber hero image"
            />
          </>
        }
      />

      {/* Section-1 */}
      <div className="relative">
        <div className="max-w-7xl mx-auto pb-8 lg:py-20 px-4 sm:px-6 lg:pg-8 text-center">
          <FadeIn>
            <h2 className="max-w-5xl mx-auto text-5xl text-alt font-extrabold my-7">
              What is <span className="text-primary">cyber</span> insurance?
            </h2>
          </FadeIn>
          <p className="max-w-xl mt-5 mx-auto text-xl text-gray-500">
            Cyber insurance, also referred to as cyber liability insurance,
            covers a broad range of risks including data loss, breaches and
            software attacks. Cyber insurance can also cover tech-related
            threats and loss of data which results in lost, stolen or
            tampered-with information. Cyber polices continue to grow in
            popularity amongst small business owners who conduct business online
            and face increasing virtual threats and risks.
          </p>
        </div>
      </div>

      {/* Section-2 */}
      <CoverageSection title="cyber" items={coverageItems} />

      {/* Section-3 */}
      <WhoNeedsInsuranceSection
        title="cyber"
        description="Many business owners think that cyber insurance is solely meant for large organizations. If your business transacts online and deals with large amounts of data or confidential information, a cyber liability policy is a crucial way to provide protection and security from the growing number of cyber threats"
      />

      {/* Section-4 */}
      <WhyIsInsuranceImportant>
        <FadeIn>
          <h2 className="text-5xl text-alt font-extrabold my-7">
            Why is a <span className="text-primary">cyber</span> policy
            important?
          </h2>
        </FadeIn>
        <p className="mt-4 text-lg text-gray-500">
          Regardless of industry or size, a cyber liability policy can protect
          your business from common digital risks associated with conducting
          business online and storing confidential information. Recently,
          various businesses and potential clients have begun to require proof
          of cyber liability insurance. A cyber liability policy can help cover
          digital risks that your business may not have anticipated.
        </p>
      </WhyIsInsuranceImportant>

      {/* Section-5 */}
      <InsuranceSection />

      {/* Section-6 */}
      <FaqSection title="Cyber insurance" faqs={faqs} />

      <BottomGraphic />
      <Footer />
    </>
  );
};

export default Cyber;
